import { useEffect, useState } from "react";
import { Menu, ArrowRight, Lock, ChevronDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useVisitor } from "@/lib/visitor";
import { intentById, audienceOf, MENUS } from "@/lib/intents";

const slug = (l) => l.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const linkCls = (active) => `whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium transition-colors hover:bg-[#EBF3FA] hover:text-[#0A3A6B] ${active ? "bg-[#EBF3FA] text-[#0A3A6B]" : "text-slate-600"}`;

const DesktopItem = ({ n, path }) => n.children ? (
  <DropdownMenu>
    <DropdownMenuTrigger className={`${linkCls(n.children.some((c) => c.href === path))} flex items-center gap-1 outline-none`} data-testid={`nav-${slug(n.label)}`}>
      {n.label} <ChevronDown className="h-3.5 w-3.5" />
    </DropdownMenuTrigger>
    <DropdownMenuContent className="min-w-[220px] rounded-2xl bg-white p-1.5">
      {n.children.map((c) => (
        <DropdownMenuItem key={c.href} asChild className="cursor-pointer rounded-xl px-3 py-2.5">
          <Link to={c.href} data-testid={`nav-${slug(c.label)}`}>{c.label}</Link>
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
) : (
  <Link to={n.href} className={linkCls(n.href === path)} data-testid={`nav-${slug(n.label)}`}>{n.label}</Link>
);

const MobileItem = ({ n, close }) => n.children ? (
  <div className="px-3 py-2">
    <p className="font-display text-lg text-slate-800">{n.label}</p>
    {n.children.map((c) => (
      <Link key={c.href} to={c.href} onClick={close} className="block rounded-lg px-3 py-2.5 text-slate-600 hover:bg-[#EBF3FA]" data-testid={`mobile-nav-${slug(c.label)}`}>{c.label}</Link>
    ))}
  </div>
) : (
  <Link to={n.href} onClick={close} className="rounded-lg px-3 py-3 font-display text-lg text-slate-800 hover:bg-[#EBF3FA]" data-testid={`mobile-nav-${slug(n.label)}`}>{n.label}</Link>
);

const PathButton = ({ current, onClick, testId }) => (
  <Button
    data-testid={testId}
    onClick={onClick}
    className="rounded-full bg-[#0A3A6B] px-5 text-white transition-colors hover:bg-[#1D64B4]"
  >
    {current ? (
      <span className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full" style={{ background: current.color === "#0A3A6B" ? "#38BDF8" : current.color }} />
        {current.short}
      </span>
    ) : (
      <span className="flex items-center gap-1.5">First time here? <ArrowRight className="h-4 w-4" /></span>
    )}
  </Button>
);

export const SiteHeader = () => {
  const { intent, openQuestionnaire } = useVisitor();
  const current = intentById(intent);
  const nav = MENUS[audienceOf(intent)];
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-40 transition-[background-color,box-shadow] duration-300 ${
        scrolled ? "bg-white/85 shadow-[0_1px_0_#E2E8F0] backdrop-blur-xl" : "bg-white/60 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 lg:px-10">
        <Logo />
        <nav className="hidden items-center gap-0.5 xl:flex" data-testid="main-nav">
          {nav.map((n) => <DesktopItem key={n.label} n={n} path={pathname} />)}
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/staff" data-testid="nav-staff-hub" title="Staff Hub"
            className="hidden h-9 w-9 place-items-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-[#0A3A6B] sm:grid">
            <Lock className="h-4 w-4" />
          </Link>
          <div className="hidden sm:block">
            <PathButton current={current} onClick={() => openQuestionnaire()} testId="header-path-button" />
          </div>
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="xl:hidden" data-testid="mobile-menu-button"><Menu /></Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] bg-white">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <div className="mt-10 flex flex-col gap-1">
                {nav.map((n) => <MobileItem key={n.label} n={n} close={() => setMenuOpen(false)} />)}
                <Link to="/staff" onClick={() => setMenuOpen(false)} className="rounded-lg px-3 py-3 text-sm text-slate-500 hover:bg-slate-100" data-testid="mobile-nav-staff">Staff Hub</Link>
                <div className="mt-4"><PathButton current={current} onClick={() => { setMenuOpen(false); openQuestionnaire(); }} testId="mobile-path-button" /></div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
